import { AlarmClock, BadgeCheck, BellRing, Eye, FileCheck2, Hourglass, RefreshCw, RotateCcw, ShieldAlert, UserCheck } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '@/api';
import { DOSSIER_METHODS, DOSSIER_STAGES, type DossierMethod, type DossierQueueQuery, type DossierQueueRow, type DossierStage } from '@/api/areas/dossierQueue';
import { Badge } from '@/components/Badge';
import { Callout, DataTable, Field, FilterBar, Pager, Section, StatCard, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { EmptyState, Loading } from '@/components/States';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { formatCount } from '@/lib/format';
import { useSearchParams } from '@/lib/scopedParams';
import { useApi } from '@/lib/useApi';
import { cn } from '@/lib/utils';
import { ActionDialog, SelectField } from '../compliance/ui';
import { DossierReviewSheet, RemindForm } from './DossierReviewSheet';
import { StageBadge, useFindingText, WaitAndDeadline } from './dossierShared';

const PAGE = 50;

/** A stat card that filters the list. */
function StatButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active} className={cn('grid h-full rounded-xl text-left outline-none focus-visible:ring-2 focus-visible:ring-ring', active && 'ring-2 ring-primary')}>
      {children}
    </button>
  );
}

/**
 * "Hồ sơ tên miền" (home hub, first tab): every domain waiting for its dossier before it
 * activates, most urgent first (the 7-day deadline, then waiting time). One primary action per
 * row: remind the customer, review, or view. The review panel opens as a side sheet
 * (`?open=<dossier id>`); J / K move through rows, Enter opens, N goes to the next after a decision.
 */
export function DossierQueuePage() {
  const { t } = useTranslation('dossiers');
  const { t: tc } = useTranslation();
  useDocumentTitle(t('title'));
  const [params, setParams] = useSearchParams();
  const stage = (params.get('stage') ?? '') as DossierStage | '';
  const method = (params.get('method') ?? '') as DossierMethod | '';
  const tld = params.get('tld') ?? '';
  const assignee = params.get('assignee') ?? '';
  const dueSoon = params.get('due_soon') === '1';
  const q = params.get('q') ?? '';
  const offset = Math.max(0, Number(params.get('offset') ?? 0) || 0);
  const open = params.get('open') ?? '';
  // "Theo giai đoạn" (v2.1 / reference 09): the same page of rows as cards in one column per stage.
  const board = params.get('view') === 'board';
  const [tldInput, setTldInput] = useState({ source: tld, value: tld });
  const [qInput, setQInput] = useState({ source: q, value: q });
  if (tldInput.source !== tld) setTldInput({ source: tld, value: tld });
  if (qInput.source !== q) setQInput({ source: q, value: q });
  const tldDraft = tldInput.source === tld ? tldInput.value : tld;
  const qDraft = qInput.source === q ? qInput.value : q;
  // The keyboard-highlighted row; back to the first one whenever the list changes.
  const listKey = [stage, method, tld, assignee, dueSoon, q, offset].join('|');
  const [cursorAt, setCursorAt] = useState({ key: listKey, index: 0 });
  const cursor = cursorAt.key === listKey ? cursorAt.index : 0;
  const setCursor = useCallback((next: (c: number) => number) => setCursorAt((prev) => ({ key: listKey, index: next(prev.key === listKey ? prev.index : 0) })), [listKey]);
  const [reminding, setReminding] = useState<DossierQueueRow | null>(null);
  const findingText = useFindingText();

  const query: DossierQueueQuery = {
    stage: stage || undefined,
    method: method || undefined,
    tld: tld || undefined,
    assignee: assignee || undefined,
    due_soon: dueSoon || undefined,
    q: q || undefined,
    limit: PAGE,
    offset,
  };
  const result = useApi((signal) => api.listDossierQueue(query, { signal }), [stage, method, tld, assignee, dueSoon, q, offset]);

  const set = useCallback(
    (patch: Record<string, string>) => {
      if (Object.entries(patch).every(([k, v]) => (params.get(k) ?? '') === v)) return;
      const p = new URLSearchParams(params);
      for (const [k, v] of Object.entries(patch)) {
        if (v) p.set(k, v);
        else p.delete(k);
      }
      if (!('offset' in patch) && !('open' in patch) && !('view' in patch)) p.delete('offset');
      setParams(p);
    },
    [params, setParams],
  );

  const data = result.status === 'success' ? result.data : undefined;
  const rows = useMemo(() => data?.items ?? [], [data]);
  const openIndex = rows.findIndex((r) => r.dossier_id === open);

  // Keyboard: J / K move the highlighted row, Enter opens it (not while typing or in the panel).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (open || reminding || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest('input,textarea,select,button,a,[contenteditable="true"],[role="dialog"]')) return;
      if (!rows.length) return;
      if (e.key === 'j') setCursor((c) => Math.min(rows.length - 1, c + 1));
      else if (e.key === 'k') setCursor((c) => Math.max(0, c - 1));
      else if (e.key === 'Enter' && rows[cursor]) set({ open: rows[cursor].dossier_id });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, reminding, rows, cursor, set, setCursor]);

  const primary = (r: DossierQueueRow) => {
    if (r.stage === 'AWAITING_CUSTOMER') {
      return (
        <Button
          size="sm"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            setReminding(r);
          }}
        >
          <BellRing /> {t('action.remind')}
        </Button>
      );
    }
    const review = r.stage === 'NEEDS_REVIEW';
    return (
      <Button
        size="sm"
        variant={review ? 'default' : 'ghost'}
        onClick={(e) => {
          e.stopPropagation();
          set({ open: r.dossier_id });
        }}
      >
        {review ? <FileCheck2 /> : <Eye />} {review ? t('action.review') : t('action.view')}
      </Button>
    );
  };

  const columns: Column<DossierQueueRow>[] = [
    {
      key: 'domain',
      header: t('col.domain'),
      cell: (r) => (
        <div className="grid gap-0.5">
          <Link to={`/domains/${encodeURIComponent(r.domain_name)}`} className="font-medium break-all text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
            {r.unicode_name}
          </Link>
          <span className="text-xs text-muted-foreground">{t(`operation.${r.operation ?? 'none'}`)}</span>
        </div>
      ),
    },
    {
      key: 'customer',
      header: t('col.customer'),
      cell: (r) => <span className="text-sm">{r.customer_name ?? '—'}</span>,
    },
    {
      key: 'registrant',
      header: t('col.registrant'),
      cell: (r) => (
        <div className="grid gap-0.5 text-sm">
          <span>{r.registrant_name ?? '—'}</span>
          {r.registrant_type ? <span className="text-xs text-muted-foreground">{t(`subjectType.${r.registrant_type as 'INDIVIDUAL'}`, { defaultValue: r.registrant_type })}</span> : null}
        </div>
      ),
    },
    { key: 'stage', header: t('col.stage'), cell: (r) => <StageBadge stage={r.stage} /> },
    { key: 'method', header: t('col.method'), cell: (r) => <span className="text-sm whitespace-nowrap">{r.method ? t(`method.${r.method}`) : <span className="text-muted-foreground">{t('method.none')}</span>}</span> },
    {
      key: 'finding',
      header: t('col.finding'),
      cell: (r) => {
        const f = findingText(r);
        return (
          <div className="grid max-w-xs gap-0.5">
            <Badge tone={f.tone}>{f.headline}</Badge>
            {f.reasons.length ? <span className="line-clamp-2 text-xs text-muted-foreground">{f.reasons.join(' · ')}</span> : null}
          </div>
        );
      },
    },
    { key: 'waiting', header: t('col.waiting'), cell: (r) => <WaitAndDeadline row={r} /> },
    { key: 'assignee', header: t('col.assignee'), cell: (r) => (r.assignee_name ? <span className="text-sm">{r.assignee_name}</span> : <span className="text-muted-foreground">—</span>) },
    { key: 'action', header: <span className="sr-only">{t('col.action')}</span>, cell: primary },
  ];

  const filtered = Boolean(stage || method || tld || assignee || dueSoon || q);
  const c = data?.counts;
  const statStage = (s: DossierStage) => () => set({ stage: stage === s ? '' : s, due_soon: '' });

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <Button size="sm" variant="outline" onClick={result.reload}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />
      <div className="space-y-6">
        {c ? (
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-5">
            <StatButton active={stage === 'AWAITING_CUSTOMER'} onClick={statStage('AWAITING_CUSTOMER')}>
              <StatCard label={t('stats.awaiting')} value={formatCount(c.awaiting_customer)} icon={Hourglass} tone={c.awaiting_customer ? 'pending' : 'ok'} />
            </StatButton>
            <StatButton active={stage === 'NEEDS_REVIEW'} onClick={statStage('NEEDS_REVIEW')}>
              <StatCard label={t('stats.review')} value={formatCount(c.needs_review)} icon={UserCheck} tone={c.needs_review ? 'serious' : 'ok'} />
            </StatButton>
            <StatButton active={stage === 'WARNING'} onClick={statStage('WARNING')}>
              <StatCard label={t('stats.warning')} value={formatCount(c.warning)} icon={ShieldAlert} tone={c.warning ? 'warn' : 'ok'} />
            </StatButton>
            <StatButton active={dueSoon} onClick={() => set({ due_soon: dueSoon ? '' : '1', stage: '' })}>
              <StatCard label={t('stats.dueSoon')} value={formatCount(c.due_soon)} icon={AlarmClock} tone={c.due_soon ? 'crit' : 'ok'} />
            </StatButton>
            <StatButton active={stage === 'ACTIVATED'} onClick={statStage('ACTIVATED')}>
              <StatCard label={t('stats.activatedToday')} value={formatCount(c.activated_today)} icon={BadgeCheck} tone="ok" />
            </StatButton>
          </div>
        ) : null}
        {c ? (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={stage === 'AUTO_CHECKING' ? 'default' : 'outline'} onClick={statStage('AUTO_CHECKING')}>
              {t('stats.checking')} <span className="tabular ml-1 font-semibold">{formatCount(c.auto_checking)}</span>
            </Button>
            <Button size="sm" variant={stage === 'ACTIVATING' ? 'default' : 'outline'} onClick={statStage('ACTIVATING')}>
              {t('stats.activating')} <span className="tabular ml-1 font-semibold">{formatCount(c.activating)}</span>
            </Button>
          </div>
        ) : null}
        <Callout tone="info">{t('note')}</Callout>
        <Section flush>
          <FilterBar
            className="px-5 pt-5"
            actions={
              filtered ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setParams(new URLSearchParams());
                  }}
                >
                  <RotateCcw /> {tc('action.reset')}
                </Button>
              ) : null
            }
          >
            <SelectField
              label={t('filter.stage')}
              value={stage}
              allLabel={t('filter.open')}
              options={DOSSIER_STAGES.map((s) => ({ value: s, label: t(`stage.${s}`) }))}
              onChange={(v) => set({ stage: v })}
              className="w-full sm:w-52"
            />
            <SelectField label={t('filter.method')} value={method} allLabel={t('filter.any')} options={DOSSIER_METHODS.map((m) => ({ value: m, label: t(`method.${m}`) }))} onChange={(v) => set({ method: v })} className="w-full sm:w-48" />
            <Field label={t('filter.tld')} className="w-full sm:w-32">
              <Input value={tldDraft} placeholder={t('filter.tldPlaceholder')} onChange={(e) => setTldInput({ source: tld, value: e.target.value })} onBlur={() => set({ tld: tldDraft.trim() })} onKeyDown={(e) => e.key === 'Enter' && set({ tld: tldDraft.trim() })} />
            </Field>
            <SelectField
              label={t('filter.assignee')}
              value={assignee as 'me' | 'unassigned' | ''}
              allLabel={t('filter.any')}
              options={[
                { value: 'me', label: t('filter.me') },
                { value: 'unassigned', label: t('filter.unassigned') },
              ]}
              onChange={(v) => set({ assignee: v })}
              className="w-full sm:w-40"
            />
            <Field label={t('filter.search')} className="w-full sm:w-52">
              <Input value={qDraft} placeholder={t('filter.searchPlaceholder')} onChange={(e) => setQInput({ source: q, value: e.target.value })} onBlur={() => set({ q: qDraft.trim() })} onKeyDown={(e) => e.key === 'Enter' && set({ q: qDraft.trim() })} />
            </Field>
            <label className="flex h-9 items-center gap-2 text-sm">
              <Checkbox checked={dueSoon} onCheckedChange={(v) => set({ due_soon: v === true ? '1' : '' })} />
              {t('filter.dueSoon')}
            </label>
          </FilterBar>
          {result.status === 'loading' ? (
            <Loading rows={6} />
          ) : result.status === 'error' ? (
            <div className="px-5 pb-5">
              <DataError error={result.error} onRetry={result.reload} />
            </div>
          ) : (
            <>
              <div className="flex justify-end px-5 pb-3" role="group" aria-label={t('board.label')}>
                <div className="inline-flex rounded-md border p-0.5 text-[13px]">
                  {(['list', 'board'] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      aria-pressed={(v === 'board') === board}
                      onClick={() => set({ view: v === 'board' ? 'board' : '' })}
                      className={cn('rounded px-3 py-1 font-medium', (v === 'board') === board ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}
                    >
                      {t(`board.${v}`)}
                    </button>
                  ))}
                </div>
              </div>
              {board ? (
                <StageBoard rows={rows} onOpen={(r) => set({ open: r.dossier_id })} findingText={findingText} />
              ) : (
              <DataTable
                columns={columns}
                rows={rows}
                rowKey={(r) => r.dossier_id}
                rowClassName={(r) => (rows[cursor]?.dossier_id === r.dossier_id ? 'bg-accent/60' : r.due_soon || r.overdue ? 'bg-critical-soft/40' : undefined)}
                onRowClick={(r) => set({ open: r.dossier_id })}
                empty={<EmptyState title={t('empty')}>{t('emptyHint')}</EmptyState>}
              />
              )}
              {data && data.total > 0 ? (
                <Pager
                  info={t('page', { from: offset + 1, to: Math.min(offset + PAGE, data.total), total: data.total })}
                  canPrevious={offset > 0}
                  canNext={offset + PAGE < data.total}
                  onPrevious={() => set({ offset: String(Math.max(0, offset - PAGE)) })}
                  onNext={() => set({ offset: String(offset + PAGE) })}
                />
              ) : null}
            </>
          )}
        </Section>
        <p className="hidden text-xs text-muted-foreground sm:block">{t('panel.keyboard')}</p>
      </div>
      {open ? (
        <DossierReviewSheet
          dossierId={open}
          onClose={() => set({ open: '' })}
          onChanged={result.reload}
          hasNext={openIndex >= 0 && openIndex < rows.length - 1}
          onNext={() => {
            const next = rows[openIndex + 1];
            if (next) {
              setCursor(() => openIndex + 1);
              set({ open: next.dossier_id });
            }
          }}
        />
      ) : null}
      <ActionDialog open={reminding !== null} onOpenChange={(o) => !o && setReminding(null)} title={t('remind.title')}>
        {reminding ? (
          <RemindForm
            dossierId={reminding.dossier_id}
            domain={reminding.unicode_name}
            onDone={() => {
              setReminding(null);
              result.reload();
            }}
            onCancel={() => setReminding(null)}
          />
        ) : null}
      </ActionDialog>
    </>
  );
}

/** One column per stage of the rows on this page, each dossier a card (domain, case, finding, deadline, assignee). */
function StageBoard({ rows, onOpen, findingText }: { rows: readonly DossierQueueRow[]; onOpen: (r: DossierQueueRow) => void; findingText: ReturnType<typeof useFindingText> }) {
  const { t } = useTranslation('dossiers');
  const stages = DOSSIER_STAGES.filter((st) => rows.some((r) => r.stage === st));
  if (!rows.length) return <EmptyState title={t('empty')}>{t('emptyHint')}</EmptyState>;
  return (
    <div className="grid grid-cols-1 gap-3 px-5 pb-5 md:grid-cols-2 xl:grid-cols-4" data-stage-board>
      {stages.map((st) => {
        const cards = rows.filter((r) => r.stage === st);
        return (
          <section key={st} className="min-w-0 rounded-xl border bg-muted/40 p-2.5" aria-label={t(`stage.${st}`)}>
            <header className="mb-2 flex items-center justify-between gap-2 px-1 text-[13px] font-semibold">
              <StageBadge stage={st} />
              <span className="text-muted-foreground tabular">{cards.length}</span>
            </header>
            <ul className="grid gap-2">
              {cards.map((r) => (
                <li key={r.dossier_id}>
                  <button type="button" onClick={() => onOpen(r)} className={cn('w-full rounded-lg border bg-card p-3 text-left hover:border-primary/40', (r.due_soon || r.overdue) && 'border-critical/40')}>
                    <div className="font-mono text-[10px] text-muted-foreground">{r.dossier_id.slice(-8)} · v{r.dossier_version}</div>
                    <div className="mt-1 text-sm font-semibold break-all">{r.unicode_name}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {r.operation ? t(`operation.${r.operation}`, { defaultValue: r.operation }) : '—'} · {r.registrant_name ?? r.customer_name ?? '—'}
                    </div>
                    <div className="mt-2">
                      <Badge tone={findingText(r).tone} className="max-w-full whitespace-normal">
                        {findingText(r).headline}
                      </Badge>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2 border-t pt-2 text-xs text-muted-foreground">
                      <WaitAndDeadline row={r} />
                      <span>{r.assignee_name ?? t('board.unassigned')}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
